import {
  convertEventSelectToTarget,
  DataTable,
  FormFeedBack,
  IOptions,
  ModalConfirmAction,
  SelectRemote,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddPersonToGroupUseCaseInterface,
  GetAllPersonsFromGroupUseCaseInterface,
  GetAllProfessionalsNotPaginatedUseCaseInterface,
  ImportPersonsFromAnotherGroupUseCaseInterface,
  RemovePersonFromGroupUseCaseInterface,
} from 'domain/usecases';
import { PROFILE_TYPE } from 'domain/value-objects/profile-type';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSelectedGroupTeachersListData,
  loadSelectedGroupTeachersListData,
} from 'presentation/config/store/groups/group-settings-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, CardBody, Col, Form, Row, Spinner } from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsGroupSettingsPersonsFromGroupList } from '../../../common/columns/group-settings/columns-group-settings-persons-from-group-list';
import { ImportEntitiesFromAnotherGroupModal } from '../import-entities-from-another-group-modal';

interface IProps {
  groupId: string;
}

const statusInitialStates = {
  confirmRemovalFromGroupBtnsStatus: STATUSPAGE.DONE,
  importTeachersFromAnotherGroupModalBtnsStatus: STATUSPAGE.DONE,
  teacherRemoteSelectInputStatus: STATUSPAGE.DONE,
  addTeacherToGroupBtnStatus: STATUSPAGE.DONE,
  teachersFromGroupListStatus: STATUSPAGE.DONE,
};

export const TeachersTabContent = ({ groupId }: IProps) => {
  // Hooks
  const { professionalService, groupService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const [
    isConfirmRemovalFromGroupModalOpen,
    setIsConfirmRemovalFromGroupModalOpen,
  ] = useState(false);
  const [
    isImportTeachersFromAnotherGroupModalOpen,
    setIsImportTeachersFromAnotherGroupModalOpen,
  ] = useState(false);
  const [
    selectedTeacherFromGroupRelationId,
    setSelectedTeacherFromGroupRelationId,
  ] = useState<string | null>(null);
  const [statusStates, setStatusStates] = useState(statusInitialStates);
  const { selectedGroupTeachersListData } = useAppSelector(
    (state) => state.groupSettings,
  );
  const [teacherSelectOptions, setTeacherSelectOptions] =
    useState<GetAllProfessionalsNotPaginatedUseCaseInterface.Result>(null);
  const teacherOptions: IOptions[] = useMemo(() => {
    return (
      teacherSelectOptions?.data?.map((teacher) => {
        const { cpf, name, id } = teacher;
        return {
          label: `${cpf} - ${name}`,
          value: id,
        };
      }) || []
    );
  }, [teacherSelectOptions]);

  // Methods
  const changeStatusState = useCallback(
    <T extends keyof typeof statusInitialStates>(key: T, value: STATUSPAGE) => {
      setStatusStates((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const toggleConfirmRemovalFromGroupModal = useCallback(
    () => setIsConfirmRemovalFromGroupModalOpen((prevState) => !prevState),
    [],
  );

  const toggleImportTeachersFromAnotherGroupModal = useCallback(
    () =>
      setIsImportTeachersFromAnotherGroupModalOpen((prevState) => !prevState),
    [],
  );

  const requestGetAllTeachersFromGroup = useCallback(
    async (params: GetAllPersonsFromGroupUseCaseInterface.Params) => {
      changeStatusState('teachersFromGroupListStatus', STATUSPAGE.LOADING);
      await groupService
        .getAllPersonsFromGroup(params)
        .then((res) => dispatch(loadSelectedGroupTeachersListData(res)))
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSelectedGroupTeachersListData());
        })
        .finally(() =>
          changeStatusState('teachersFromGroupListStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestGetAllTeachersForSelectOptions = useCallback(
    async (params: GetAllProfessionalsNotPaginatedUseCaseInterface.Params) => {
      changeStatusState('teacherRemoteSelectInputStatus', STATUSPAGE.LOADING);
      await professionalService
        .getAllProfessionalsNotPaginated(params)
        .then((res) => setTeacherSelectOptions(res))
        .catch((err) => {
          notifyError(err.message);
          setTeacherSelectOptions(null);
        })
        .finally(() =>
          changeStatusState('teacherRemoteSelectInputStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestAddTeacherToGroup = useCallback(
    async (params: AddPersonToGroupUseCaseInterface.Params) => {
      changeStatusState('addTeacherToGroupBtnStatus', STATUSPAGE.LOADING);
      await groupService
        .addPersonToGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllTeachersFromGroup({
            groupId,
            personType: PROFILE_TYPE.TEACHER,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState('addTeacherToGroupBtnStatus', STATUSPAGE.DONE),
        );
    },
    [],
  );

  const requestRemoveTeacherFromGroup = useCallback(
    async (params: RemovePersonFromGroupUseCaseInterface.Params) => {
      changeStatusState(
        'confirmRemovalFromGroupBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .removePersonFromGroup(params)
        .then((res) => notifySuccess(res.message))
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'confirmRemovalFromGroupBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  const requestImportTeachersFromAnotherGroup = useCallback(
    async (params: ImportPersonsFromAnotherGroupUseCaseInterface.Params) => {
      changeStatusState(
        'importTeachersFromAnotherGroupModalBtnsStatus',
        STATUSPAGE.LOADING,
      );
      await groupService
        .importPersonsFromAnotherGroup(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllTeachersFromGroup({
            groupId,
            personType: PROFILE_TYPE.TEACHER,
          });
        })
        .catch((err) => notifyError(err.message))
        .finally(() =>
          changeStatusState(
            'importTeachersFromAnotherGroupModalBtnsStatus',
            STATUSPAGE.DONE,
          ),
        );
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      teacher: null,
    },

    validationSchema: Yup.object({
      teacher: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: values.teacher?.value,
        group: groupId,
        personType: PROFILE_TYPE.TEACHER,
      };

      requestAddTeacherToGroup(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllTeachersFromGroup({
      groupId,
      personType: PROFILE_TYPE.TEACHER,
    });
  }, [groupId]);

  return (
    <>
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <CardBody>
          <Row>
            <Col lg={7} className="mb-3">
              <SelectRemote
                isClearable
                name="teacher"
                clearOptions={() => setTeacherSelectOptions(null)}
                functionSearch={(searchString) => {
                  if (searchString.length === 0) {
                    setTeacherSelectOptions(null);
                    return;
                  }

                  if (searchString.length < 3) return;

                  requestGetAllTeachersForSelectOptions({
                    profileType: PROFILE_TYPE.TEACHER,
                    query: searchString,
                  });
                }}
                inputId={'teacher'}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('teacher', e),
                  );
                }}
                onBlur={(e) => {
                  setTeacherSelectOptions(null);
                }}
                options={teacherOptions}
                placeholder={`Selecione um ${getLegends('PROFESSOR')}`}
                value={validation.values.teacher}
                loading={
                  statusStates.teacherRemoteSelectInputStatus ===
                  STATUSPAGE.LOADING
                }
                isInvalid={
                  !!(validation.touched?.teacher && validation.errors?.teacher)
                }
                isDisabled={
                  statusStates.addTeacherToGroupBtnStatus === STATUSPAGE.LOADING
                }
              />
              <FormFeedBack
                error={String(validation.errors?.teacher)}
                isInvalid={
                  !!(validation.touched?.teacher && validation.errors?.teacher)
                }
              />
            </Col>
            <Col lg={5} className="mb-3">
              <div className="d-flex justify-content-end gap-4">
                <Button
                  type="submit"
                  disabled={
                    statusStates.addTeacherToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  {statusStates.addTeacherToGroupBtnStatus ===
                    STATUSPAGE.LOADING && (
                    <Spinner size="sm">{['Carregando...']}</Spinner>
                  )}
                  Adicionar
                </Button>
                <Button
                  type="button"
                  onClick={() => toggleImportTeachersFromAnotherGroupModal()}
                  disabled={
                    statusStates.addTeacherToGroupBtnStatus ===
                    STATUSPAGE.LOADING
                  }
                  color="primary"
                >
                  Importar de outro {getLegends('GRUPO')}
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <DataTable
              noDataBg={noDataBg}
              actions={[
                {
                  id: 1,
                  name: 'remove',
                  label: 'Remover',
                  icon: 'ri-indeterminate-circle-fill',
                  classNameColorIcon: 'danger',
                  onPress: (cellProps) => {
                    setSelectedTeacherFromGroupRelationId(cellProps.relationId);
                    toggleConfirmRemovalFromGroupModal();
                  },
                },
              ]}
              canSearch
              columns={makeColumnsGroupSettingsPersonsFromGroupList()}
              data={selectedGroupTeachersListData}
              loading={
                statusStates.teachersFromGroupListStatus === STATUSPAGE.LOADING
              }
            />
          </Row>
        </CardBody>
      </Form>

      <ModalConfirmAction
        modalTheme="danger"
        title="Deseja remover?"
        actionBtnLabel="Remover"
        isOpen={isConfirmRemovalFromGroupModalOpen}
        toggle={toggleConfirmRemovalFromGroupModal}
        onConfirm={() =>
          requestRemoveTeacherFromGroup({
            relationId: selectedTeacherFromGroupRelationId,
          })
        }
        loading={
          statusStates.confirmRemovalFromGroupBtnsStatus === STATUSPAGE.LOADING
        }
      />

      <ImportEntitiesFromAnotherGroupModal
        isOpen={isImportTeachersFromAnotherGroupModalOpen}
        toggle={toggleImportTeachersFromAnotherGroupModal}
        importedEntitiesTitle={getLegends('PROFESSOR', true)}
        buttonsLoading={
          statusStates.importTeachersFromAnotherGroupModalBtnsStatus ===
          STATUSPAGE.LOADING
        }
        requestImportEntitiesFromAnotherGroup={
          requestImportTeachersFromAnotherGroup
        }
        importedPersonType={PROFILE_TYPE.TEACHER}
        targetGroupId={groupId}
      />
    </>
  );
};
